/* eslint-disable no-debugger */
import { useEffect, useState } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';
import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import useSelectOrgUnitData from '@usas/ui-web/state/selectOrgUnit/UseSelectOrgUnitData';
import useClubStaffData from "../../../../state/clubStaff/UseClubStaffData";
import useLscBoardMemberData from '../../../../state/lscBoardMembers/UseLscBoardMemberData';

import useTouchpointTypeData from '../../../../state/touchpointType/UseTouchpointTypeData';
import useTouchpointSubjectData from '../../../../state/touchpointSubject/UseTouchpointSubjectData';
import useLscTouchpointData from '../../../../state/lscTouchpoints/UseLscTouchpointsData';
import { navLinks } from '../../UseMemberAdministration';

import validate from './AddLSCTouchpointValidation';

const useAddLSCTouchpoint = () => {
  const navigate = useNavigate();
  const { touchpointTypeState } = useTouchpointTypeData();
  const { touchpointSubjectState } = useTouchpointSubjectData();
  const { selectOrgUnitState } = useSelectOrgUnitData();
  const { lscTouchpointState, postLscTouchpoint } = useLscTouchpointData();
  const { getClubStaffMembers } = useClubStaffData();
  const { getLscBoardMembers } = useLscBoardMemberData();
  const [clubStaffOptionsState, setClubStaffOptionsState] = useState({ items: [], areItemsLoaded: false });
  const [lscBoardMemberOptionsState, setLscBoardMemberOptionsState] = useState({ items: [], areItemsLoaded: false });

  const { formState, errorState, isSubmitting, updateFormState, onFormValueChanged, onValueTextPairChanged, handleSubmit, setErrors, setFormData, setIsDirty
  } = useForm(getInitialFormState, onSubmitFormCallback, validate);

  function onSubmitFormCallback() {
    const touchpointObj = {
      touchpointTypeId: formState.touchpointTypeId,
      touchpointSubjectId: formState.touchpointSubjectId,
      touchpointNote: formState.touchpointNote,
      touchpointDate: formState.touchpointDate,
      followUpDate: formState.followUpDate === Constants.BLANK_DATE_STRING ? null : formState.followUpDate,
      staffMemberId: formState.memberId || '',
      hasCustomerSatisfactionSurvey: formState.hasCustomerSatisfactionSurvey || false
    };

    const postLscTouchpointPromise = postLscTouchpoint(selectOrgUnitState.orgUnitId, touchpointObj);
    if (postLscTouchpointPromise !== null) {
      postLscTouchpointPromise.then((newState) => {
        if (newState !== null) {
          //nothing to do here
          navigate(navLinks.LSC_TOUCHPOINTS);
        }
      }).catch((e) => {
        //TODO context error
      });
    }
  }

  const onSaveClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    handleSubmit();
    // if (isDirty === false) {
    //   setState({ ...state, tryRedirect: true });
    // }
  };

  const onCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    navigate(navLinks.LSC_TOUCHPOINTS);
  };

  useEffect(() => {
    if (selectOrgUnitState.orgUnitId) {
      const getClubStaffMembersPromise = getClubStaffMembers(selectOrgUnitState.orgUnitId, 2, false);

      if (getClubStaffMembersPromise !== null) {
        getClubStaffMembersPromise.then((newStaffState) => {
          if (newStaffState !== null) {
            const getLscBoardMembersPromise = getLscBoardMembers(selectOrgUnitState.orgUnitId);

            if (getLscBoardMembersPromise !== null) {
              getLscBoardMembersPromise.then((newBoardMemberState) => {
                if (newBoardMemberState !== null) {
                  let combinedItems = [];
                  let staffItems = [];
                  let boardItems = [];

                  newBoardMemberState.arrayData.forEach(boardMember => {  
                    let cntCurrentRoles = 0;
                    boardMember.orgRoles.forEach(role => {
                      if (new Date(role.expirationDate) >= new Date()) {
                        cntCurrentRoles++;
                      } 
                    });
                    if (cntCurrentRoles > 0) {
                      boardItems.push(boardMember);
                    }
                  }); 
                  
                  newStaffState.arrayData.forEach(staff => {  
                    let cntCurrentRoles = 0;
                    staff.personOrgRole.forEach(role => {
                      if (new Date(role.expirationDate) >= new Date()) {
                        cntCurrentRoles++;
                      }
                    });
                    if (cntCurrentRoles > 0) {
                      staff.lastName = staff.lastName + ' (Staff Only)';
                      staffItems.push(staff);
                    }
                  });

                  combinedItems = boardItems.concat(staffItems);

                  //sort by last name then first name
                  combinedItems.sort((a, b) => {
                    if (a.lastName < b.lastName) return -1;
                    if (a.lastName > b.lastName) return 1;
                    return 0;
                  });

                  const items = combinedItems.map(x =>  
                    {
                      return { id: x.memberId, name: `${x.firstName} ${x.lastName}` } 
                    }
                  );

                  //remove duplicates
                  const uniqueItems = Array.from(new Set(items.map(item => item.id)))
                    .map(id => {
                      return items.find(item => item.id === id);
                    });

                  uniqueItems.unshift({ id: '', name: '--' });

                  setLscBoardMemberOptionsState({
                    ...lscBoardMemberOptionsState,
                    items: uniqueItems,
                    areItemsLoaded: true
                  });
                }
              }).catch((e) => {
                //TODO context error
              });
            }
          }
        }).catch((e) => {
          //TODO context error
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectOrgUnitState]);

  function getInitialFormState() {
    return {
      lscTouchpointId: Constants.DEFAULT_ID,
      touchpointTypeId: Constants.DEFAULT_ID,
      touchpointTypeName: '',
      touchpointSubjectId: Constants.DEFAULT_ID,
      touchpointSubjectName: '',
      touchpointNote: '',
      touchpointDate: '',
      memberId: '',
      memberName: '',
      followUpDate: Constants.BLANK_DATE_STRING,
      hasCustomerSatisfactionSurvey: false
    };
  }

  return {
    formState,
    errorState,
    touchpointTypeState,
    touchpointSubjectState,
    lscTouchpointState,
    clubStaffOptionsState,
    lscBoardMemberOptionsState,
    handleSubmit,
    isLoading: false,
    onFormValueChanged,
    onValueTextPairChanged,
    onSaveClicked,
    onCancelClicked
  }
}

export default useAddLSCTouchpoint;